import {produce} from 'immer';
import {useSetAtom} from 'jotai';
import {isNil, orderBy, unionBy} from 'lodash';
import {ChangeEvent, useMemo, useState} from 'react';
import styled from 'styled-components';
import {HighlightText} from '@/components/HighlightText';
import {ArrowLeftIconComponent} from '@/components/Icons';
import {LoadingBar} from '@/components/LoadingBar';
import {Typography, typographyStyle} from '@/components/Typography';
import {
  AddressItemType, AddressSearchHistoryItem, AddressSearchItem, SubwaySearchItem
} from '@/map/@types';
import {useSearchAddress} from '@/map/api';
import {mobileAddressSearchHistoryAtom} from '@/map/atom';
import {getAddressItemKeyword, getAddressMainKey, getAddressSubKey} from '@/map/utils';
import {Flexbox, HorizontalFullContainer, PaddingBox} from '@/styled-components';
import {palette} from '@/styles';
import {NoData} from '../AnalysisDrawer/Report/NoData';
import {useAndroidBackHandler} from '../AndroidBackHandler/useAndroidBackHandler';
import {FullPageModal} from '../FullPageModal';
import LocationIcon from './search-location.svg';
import SubwayIcon from './search-subway.svg';
import {SearchHistoryList} from './SearchHistory';
import {SearchInput} from './SearchInput';

interface IProps {
  initialValue?: string;
  onSelected(selected: ActiveSearchItem): void;
  onPrev(): void;
}

interface ActiveSearchItem {
  keyword: string;
  item: AddressItemType;
}

export const AddressSearch = ({initialValue, onSelected, onPrev}: IProps) => {
  const [keyword, setKeyword] = useState<string>(initialValue || '');
  const {data, isPending} = useSearchAddress(keyword);

  const setHistory = useSetAtom(mobileAddressSearchHistoryAtom);

  const sortedSubway = useMemo(
    () =>
      data?.subway
        ? orderBy(data.subway, ['name', 'line'], ['asc', 'asc'])
        : [],
    [data?.subway]
  );

  const duplicateFreeAddr = useMemo(
    () =>
      data?.address
        ? unionBy(data?.address, (item) => getAddressItemKeyword(item, keyword))
        : [],
    [data?.address, keyword]
  );

  const hasSearchResult =
    !!data && (!!sortedSubway?.length || !!duplicateFreeAddr?.length);

  const onKeywordClear = () => {
    setKeyword('');
  };

  useAndroidBackHandler(() => {
    if (keyword?.length) {
      onKeywordClear();
    } else {
      onPrev();
    }

    return true;
  });

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value);
  };

  const handlePrev = () => {
    if (keyword?.length) {
      onKeywordClear();
    } else {
      onPrev();
    }
  };

  const setSearchHistory = (
    historyItem: AddressSearchHistoryItem,
    replaceIndex?: number
  ) => {
    if (!historyItem) {
      return;
    }

    setHistory(
      produce((draft) => {
        const item: AddressSearchHistoryItem = {
          createdTime: new Date().getTime(),
          ...historyItem,
        };

        const dupIndex = isNil(replaceIndex)
          ? draft.findIndex((i: AddressSearchHistoryItem) => {
              if (item.keyword) {
                return i.keyword === item.keyword;
              } else {
                return (
                  !!i.mainKey &&
                  !!item.mainKey &&
                  (i.origin as AddressSearchItem)[i.mainKey] ===
                    (item.origin as AddressSearchItem)[item.mainKey]
                );
              }
            })
          : replaceIndex;

        if (dupIndex >= 0) {
          draft.splice(dupIndex, 1);
        }

        draft.unshift(item);
        draft.slice(0, 10);
      })
    );
  };

  const findSearchItem = (item: AddressItemType) => {
    const historyItem: AddressSearchHistoryItem = {
      origin: item,
    };
    let mainKeyword = '';

    if (!('line' in item) && item.location) {
      const addressItem = item as AddressSearchItem;

      historyItem.mainKey = getAddressMainKey(addressItem, keyword);
      historyItem.subKey = getAddressSubKey(addressItem, keyword);

      mainKeyword = item[historyItem.mainKey];
    } else {
      mainKeyword = (item as SubwaySearchItem).name;
    }

    setSearchHistory(historyItem);

    onSelected({
      keyword: mainKeyword,
      item,
    });
  };

  const onClickSearchItem = (item: AddressItemType) => () => {
    findSearchItem(item);
  };

  const onExampleClick = (example: ActiveSearchItem) => {
    onSelected(example);
  };

  const onHistoryItemClick = (
    historyItem: AddressSearchHistoryItem,
    index: number
  ) => {
    if (!historyItem) {
      return;
    }

    let mainKeyword = '';

    if (historyItem.origin) {
      if ('line' in historyItem.origin) {
        mainKeyword = historyItem.origin.name;
      } else {
        mainKeyword = (historyItem.origin as any)[historyItem.mainKey as any];
      }

      setSearchHistory(historyItem, index);

      onSelected({
        keyword: mainKeyword,
        item: historyItem.origin,
      });
    }
  };

  return (
    <FullPageModal>
      <Root>
        <SearchBar>
          <BackButton color={palette.black} onClick={handlePrev} />
          <SearchInput
            value={keyword}
            name="keyword"
            onChange={onChange}
            onClear={onKeywordClear}
          />
        </SearchBar>
        <ScrollBody>
          {hasSearchResult ? (
            <SearchList>
              {sortedSubway.map((i, idx) => (
                <Item
                  key={idx}
                  item={i}
                  keyword={keyword}
                  onClick={onClickSearchItem(i)}
                />
              ))}
              {duplicateFreeAddr?.map((i, idx) => (
                <Item
                  key={idx}
                  item={i}
                  keyword={keyword}
                  onClick={onClickSearchItem(i)}
                />
              ))}
            </SearchList>
          ) : !keyword ? (
            <SearchHistoryList
              onHistoryItemClick={onHistoryItemClick}
              onExampleClick={onExampleClick}
            />
          ) : isPending ? (
            <PaddingBox top={40}>
              <LoadingBar color={palette.textSecondary} size={24} />
            </PaddingBox>
          ) : (
            <PaddingBox top={28}>
              <NoData text="입력하신 검색어에 맞는 검색 결과가 없습니다." />
            </PaddingBox>
          )}
        </ScrollBody>
      </Root>
    </FullPageModal>
  );
};

const Item = ({
  item,
  keyword,
  onClick,
}: {
  item: AddressItemType;
  keyword: string;
  onClick(): void;
}) => {
  const itemKeyword = getAddressItemKeyword(item, keyword);
  const isSubway = 'line' in item;

  return (
    <SearchItem onClick={onClick}>
      <img
        src={isSubway ? SubwayIcon : LocationIcon}
        alt={isSubway ? '지하철' : '장소'}
      />
      <ItemKeyword style={isSubway ? {maxWidth: 164} : undefined}>
        <HighlightText
          query={keyword}
          text={itemKeyword || ''}
          highlightColor={palette.teal100}
        />
      </ItemKeyword>
      {isSubway && <SubwayLine>{item.line}</SubwayLine>}
    </SearchItem>
  );
};

const Root = styled(HorizontalFullContainer)``;

const ScrollBody = styled.div`
  width: 100%;
  flex-grow: 1;
  overflow: auto;
`;

const SearchBar = styled(Flexbox)`
  height: 60px;
  padding: 0 16px;
  gap: 8px;
  flex-shrink: 0;
`;

const BackButton = styled(ArrowLeftIconComponent)`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  cursor: pointer;
`;

const SearchList = styled.div`
  padding-top: 11px;
`;

const SearchItem = styled(Flexbox)`
  padding: 14px 16px;
  gap: 8px;
  img {
    margin-right: 4px;
  }
  ${{...typographyStyle.medium16}}
`;

const ItemKeyword = styled(Typography).attrs({ellipsis: true, as: 'div'})``;

const SubwayLine = styled(Typography).attrs({ellipsis: true, as: 'div'})`
  color: ${palette.gray500};
  max-width: 120px;
`;
